import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Loader2 } from 'lucide-react';
import { formatIBAN, cleanIBAN, lookupBankDetails } from '../../utils/ibanHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateRegisterNew = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Address Autocomplete States
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  
  // IBAN Validation States
  const [ibanValidating, setIbanValidating] = useState(false);
  const [ibanValidationResult, setIbanValidationResult] = useState(null);
  
  // Info Modal State
  const [showKleinunternehmerInfo, setShowKleinunternehmerInfo] = useState(false);
  
  // Local format validation states
  const [vatFormatValid, setVatFormatValid] = useState(null);
  const [taxNumberFormatValid, setTaxNumberFormatValid] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Personal Data
    first_name: '',
    last_name: '',
    email: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    state: '',
    country: 'Deutschland',
    
    // Step 2: Tax & Payout
    tax_status: 'small_business',
    vat_id: '',
    tax_number: '',  // Alternative to vat_id
    vat_mode: 'small_business',  // NEW: Kleinunternehmer vs. Regelbesteuert
    vat_rate: 19,  // NEW: USt-Satz (19% default)
    payout_method: 'sepa_manual',
    iban: '',
    iban_formatted: '',  // NEW: Formatted IBAN
    bank_name: '',  // NEW: Auto-detected
    bic: '',  // NEW: Auto-detected
    bank_country: '',  // NEW: Auto-detected
    account_holder: '',
    payout_email: '',
    
    // Step 3: Additional Info & Legal
    company_name: '',
    website_or_social: '',
    marketing_channel: '',
    accepted_terms: false,
    accepted_privacy: false,
    accepted_affiliate_terms: false,
    confirmed_self_employed: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    
    // Trigger address search when street input changes
    if (name === 'street' && value.length >= 3) {
      searchAddress(value);
    } else if (name === 'street' && value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Search for addresses using Photon API
  const searchAddress = async (query) => {
    if (!query || query.length < 3) return;
    
    setAddressSearching(true);
    try {
      const response = await axios.get('https://photon.komoot.io/api/', {
        params: {
          q: query + ', Deutschland',
          lang: 'de',
          limit: 7
        }
      });
      
      const suggestions = response.data.features
        .filter(feature => feature.properties.street || (feature.properties.name && feature.properties.type === 'street'))
        .map(feature => ({
          street: feature.properties.street || feature.properties.name,
          housenumber: feature.properties.housenumber || '',
          postcode: feature.properties.postcode || '',
          city: feature.properties.city || '',
          state: feature.properties.state || ''
        }));
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (err) {
      console.error('Address search failed:', err);
    } finally {
      setAddressSearching(false);
    }
  };

  // Select an address from suggestions
  const selectAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      street: suggestion.street,
      house_number: suggestion.housenumber || prev.house_number,
      postal_code: suggestion.postcode || prev.postal_code,
      city: suggestion.city || prev.city,
      state: suggestion.state || prev.state
    }));
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Local format validation for USt-IdNr
  const validateVatIdFormat = (vatId) => {
    if (!vatId) {
      setVatFormatValid(null);
      return;
    }
    
    // Format: 2 letters + 8-12 digits
    const vatPattern = /^[A-Z]{2}[0-9]{8,12}$/;
    const isValid = vatPattern.test(vatId.replace(/\s/g, '').toUpperCase());
    setVatFormatValid(isValid);
  };

  // Local format validation for Steuernummer
  const validateTaxNumberFormat = (taxNumber) => {
    if (!taxNumber) {
      setTaxNumberFormatValid(null);
      return;
    }
    
    // Format: At least 8 characters, numbers and slashes
    const taxPattern = /^[0-9\/]{8,}$/;
    const isValid = taxPattern.test(taxNumber.replace(/\s/g, ''));
    setTaxNumberFormatValid(isValid);
  };

  // Validate IBAN and get bank info
  const validateIban = async (iban) => {
    if (!iban || iban.replace(/\s/g, '').length < 15) {
      setIbanValidationResult(null);
      return;
    }

    setIbanValidating(true);
    try {
      const response = await axios.post(`${API_URL}/api/affiliate/validate-iban`, {
        iban: iban
      });
      setIbanValidationResult(response.data);
      
      // Auto-format IBAN if valid
      if (response.data.valid && response.data.formatted_iban) {
        setFormData(prev => ({
          ...prev,
          iban: response.data.formatted_iban
        }));
      }
    } catch (err) {
      setIbanValidationResult({
        valid: false,
        message: err.response?.data?.detail || 'Validierung fehlgeschlagen'
      });
    } finally {
      setIbanValidating(false);
    }
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.first_name || !formData.last_name || !formData.email ||
          !formData.street || !formData.house_number || !formData.postal_code || !formData.city) {
        setError('Bitte alle Pflichtfelder ausfüllen');
        return false;
      }
      // Basic email validation
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        setError('Bitte gültige E-Mail-Adresse eingeben');
        return false;
      }
    } else if (currentStep === 2) {
      // Business: Either vat_id OR tax_number required
      if (formData.tax_status === 'business' && !formData.vat_id && !formData.tax_number) {
        setError('USt-IdNr. oder Steuernummer ist für Gewerblich/Unternehmer erforderlich');
        return false;
      }
      // IBAN is OPTIONAL - no validation required
      // Affiliate can add bank details later
    } else if (currentStep === 3) {
      if (!formData.accepted_terms || !formData.accepted_privacy ||
          !formData.accepted_affiliate_terms || !formData.confirmed_self_employed) {
        setError('Bitte alle rechtlichen Bestätigungen akzeptieren');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/affiliate/register`, formData);
      // Navigate to success page with data
      navigate('/affiliate/register/success', {
        state: {
          affiliate_code: response.data.affiliate_code,
          affiliate_link: response.data.affiliate_link,
          status: response.data.status
        }
      });
    } catch (err) {
      // Handle different error formats
      let errorMessage = 'Registrierung fehlgeschlagen';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // If detail is an array (FastAPI validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } 
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
        // If detail is a string
        else {
          errorMessage = detail;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: 'Persönliche Daten' },
    { number: 2, title: 'Steuern & Auszahlung' },
    { number: 3, title: 'Zusatzinfos & Rechtliches' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Progress Bar */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          <div className="flex justify-between items-center mb-8">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all
                    ${currentStep >= step.number
                      ? 'bg-[#0077FF] text-white'
                      : 'bg-gray-200 text-gray-500'}
                  `}>
                    {currentStep > step.number ? <Check className="h-5 w-5" /> : step.number}
                  </div>
                  <span className={`
                    text-xs mt-2 text-center hidden sm:block
                    ${currentStep >= step.number ? 'text-[#0077FF] font-medium' : 'text-gray-500'}
                  `}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`
                    flex-1 h-1 mx-2 transition-all
                    ${currentStep > step.number ? 'bg-[#0077FF]' : 'bg-gray-200'}
                  `} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Current Step Title - Mobile */}
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-6 sm:hidden">
            {steps[currentStep - 1].title}
          </h2>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="space-y-6">
            {/* Step 1: Personal Data */}
            {currentStep === 1 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="Max"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                    placeholder="max@example.com"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Straße *
                    </label>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => formData.street.length >= 3 && setShowSuggestions(addressSuggestions.length > 0)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="Musterstraße"
                      autoComplete="off"
                    />
                    {addressSearching && (
                      <div className="absolute right-3 top-11">
                        <Loader2 className="h-4 w-4 text-[#0077FF] animate-spin" />
                      </div>
                    )}
                    
                    {/* Address Suggestions Dropdown */}
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => selectAddress(suggestion)}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 dark:text-gray-100">
                              {suggestion.street} {suggestion.housenumber}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {suggestion.postcode} {suggestion.city}
                              {suggestion.state && `, ${suggestion.state}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hausnr. *
                    </label>
                    <input
                      type="text"
                      name="house_number"
                      value={formData.house_number}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="123"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      PLZ *
                    </label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="12345"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Stadt *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                      placeholder="Berlin"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bundesland
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                    placeholder="z.B. Bayern, Berlin, Nordrhein-Westfalen"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Land
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800"
                    readOnly
                  />
                </div>
              </>
            )}

            {/* Step 2: Tax & Payout */}
            {currentStep === 2 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Steuerstatus * <span className="text-gray-500 text-xs">(wichtig für Abrechnung)</span>
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-gray-800 transition-colors" style={{ borderColor: formData.tax_status === 'small_business' ? '#0077FF' : '#E5E7EB' }}>
                      <input
                        type="radio"
                        name="tax_status"
                        value="small_business"
                        checked={formData.tax_status === 'small_business'}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">Kleinunternehmer (§19 UStG)</span>
                          <button
                            type="button"
                            onClick={() => setShowKleinunternehmerInfo(true)}
                            className="text-blue-500 hover:text-blue-700 transition-colors"
                            title="Mehr Informationen"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Keine Umsatzsteuer auf Provisionen</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-gray-800 transition-colors" style={{ borderColor: formData.tax_status === 'business' ? '#0077FF' : '#E5E7EB' }}>
                      <input
                        type="radio"
                        name="tax_status"
                        value="business"
                        checked={formData.tax_status === 'business'}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Gewerblich / Unternehmer</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Mit Umsatzsteuer (USt-IdNr. oder Steuernummer erforderlich)</div>
                      </div>
                    </label>
                  </div>
                </div>

                {formData.tax_status === 'business' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        USt-IdNr. <span className="text-gray-500 text-xs">(optional - z.B. DE123456789)</span>
                      </label>
                      <input
                        type="text"
                        name="vat_id"
                        value={formData.vat_id}
                        onChange={(e) => {
                          handleChange(e);
                          validateVatIdFormat(e.target.value);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                        placeholder="DE123456789"
                      />
                      {vatFormatValid !== null && formData.vat_id && (
                        <div className={`mt-2 p-3 rounded-lg text-sm ${
                          vatFormatValid 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          <div className="font-medium">
                            {vatFormatValid ? '✓ Format korrekt' : '✗ Format ungültig'}
                          </div>
                          {!vatFormatValid && (
                            <div className="text-xs mt-1">
                              Format: 2 Buchstaben + 8-12 Ziffern (z.B. DE123456789)
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        ℹ️ Wenn vorhanden, bitte angeben. Ansonsten geben Sie unten Ihre Steuernummer an.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Steuernummer {!formData.vat_id && <span className="text-red-500">*</span>} 
                        <span className="text-gray-500 text-xs ml-2">(z.B. 12/345/67890)</span>
                      </label>
                      <input
                        type="text"
                        name="tax_number"
                        value={formData.tax_number}
                        onChange={(e) => {
                          handleChange(e);
                          validateTaxNumberFormat(e.target.value);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                        placeholder="12/345/67890"
                      />
                      {taxNumberFormatValid !== null && formData.tax_number && (
                        <div className={`mt-2 p-3 rounded-lg text-sm ${
                          taxNumberFormatValid 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          <div className="font-medium">
                            {taxNumberFormatValid ? '✓ Format korrekt' : '✗ Format ungültig'}
                          </div>
                          {!taxNumberFormatValid && (
                            <div className="text-xs mt-1">
                              Mindestens 8 Zeichen, Zahlen und Schrägstriche (z.B. 12/345/67890)
                            </div>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.vat_id 
                          ? 'Optional: Ihre deutsche Steuernummer' 
                          : 'Pflicht, wenn keine USt-IdNr. vorhanden'}
                      </p>
                    </div>
                  </>
                )}


                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Auszahlungsdaten</h3>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Auszahlungsmethode *
                    </label>
                    <select
                      name="payout_method"
                      value={formData.payout_method}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                    >
                      <option value="manual">Manuelle Überweisung (SEPA)</option>
                      <option value="paypal">PayPal</option>
                    </select>
                  </div>

                  {formData.payout_method === 'manual' && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          IBAN *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            name="iban"
                            value={formData.iban}
                            onChange={(e) => {
                              handleChange(e);
                              if (e.target.value.replace(/\s/g, '').length >= 15) {
                                validateIban(e.target.value);
                              } else {
                                setIbanValidationResult(null);
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                            placeholder="DE89 3704 0044 0532 0130 00"
                          />
                          {ibanValidating && (
                            <div className="absolute right-3 top-3">
                              <Loader2 className="h-5 w-5 text-[#0077FF] animate-spin" />
                            </div>
                          )}
                        </div>
                        {ibanValidationResult && (
                          <div className={`mt-2 p-3 rounded-lg text-sm ${
                            ibanValidationResult.valid 
                              ? 'bg-green-50 text-green-700 border border-green-200' 
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            <div className="font-medium">
                              {ibanValidationResult.valid ? '✓ ' : '✗ '}
                              {ibanValidationResult.message}
                            </div>
                            {ibanValidationResult.valid && ibanValidationResult.bank_short_name && (
                              <div className="mt-2 space-y-1 text-xs">
                                <div>🏦 <strong>Bank:</strong> {ibanValidationResult.bank_short_name || ibanValidationResult.bank_name || 'Unbekannt'}</div>
                                {ibanValidationResult.bic && (
                                  <div>📋 <strong>BIC:</strong> {ibanValidationResult.bic}</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kontoinhaber <span className="text-gray-500 text-xs">(optional)</span>
                        </label>
                        <input
                          type="text"
                          name="account_holder"
                          value={formData.account_holder}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                          placeholder="Max Mustermann"
                        />
                      </div>
                    </>
                  )}

                  {formData.payout_method === 'paypal' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        PayPal E-Mail *
                      </label>
                      <input
                        type="email"
                        name="payout_email"
                        value={formData.payout_email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                        placeholder="paypal@example.com"
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Step 3: Additional Info & Legal */}
            {currentStep === 3 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Firmenname <span className="text-gray-500 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                    placeholder="Ihre Firma GmbH"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Website oder Social Media <span className="text-gray-500 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="website_or_social"
                    value={formData.website_or_social}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                    placeholder="https://example.com oder @username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marketing-Kanal <span className="text-gray-500 text-xs">(optional)</span>
                  </label>
                  <select
                    name="marketing_channel"
                    value={formData.marketing_channel}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#0077FF] focus:border-transparent"
                  >
                    <option value="">Bitte wählen</option>
                    <option value="website">Website / Blog</option>
                    <option value="social_media">Social Media</option>
                    <option value="referral">Persönliche Empfehlung</option>
                    <option value="offline">Offline / Events</option>
                  </select>
                </div>

                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">Rechtliche Bestätigungen *</h3>
                  
                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="accepted_terms"
                        checked={formData.accepted_terms}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Ich akzeptiere die <a href="/terms" target="_blank" className="text-[#0077FF] underline">AGB</a>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="accepted_privacy"
                        checked={formData.accepted_privacy}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Ich akzeptiere die <a href="/datenschutz" target="_blank" className="text-[#0077FF] underline">Datenschutzerklärung</a>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="accepted_affiliate_terms"
                        checked={formData.accepted_affiliate_terms}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Ich akzeptiere die <a href="/affiliate-terms" target="_blank" className="text-[#0077FF] underline">Affiliate-Bedingungen</a>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="confirmed_self_employed"
                        checked={formData.confirmed_self_employed}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Ich bestätige, dass ich <strong>selbstständig/gewerblich tätig</strong> bin und berechtigt, Provisionen zu erhalten
                      </div>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 transition-colors border border-gray-300 dark:border-gray-600"
            >
              <ChevronLeft className="h-5 w-5" />
              Zurück
            </button>
          )}
          
          {currentStep < 3 ? (
            <button
              onClick={nextStep}
              className="flex items-center gap-2 px-6 py-3 bg-[#0077FF] text-white rounded-lg hover:bg-[#0066DD] transition-colors ml-auto"
            >
              Weiter
              <ChevronRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-[#0077FF] text-white rounded-lg hover:bg-[#0066DD] transition-colors ml-auto disabled:bg-gray-400"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  Registrierung abschließen
                  <Check className="h-5 w-5" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Kleinunternehmer Info Modal */}
      {showKleinunternehmerInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowKleinunternehmerInfo(false)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">§19 UStG - Kleinunternehmerregelung</h2>
              <button
                onClick={() => setShowKleinunternehmerInfo(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p className="font-medium text-lg">Als Kleinunternehmer:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Keine Umsatzsteuer auf Provisionen</li>
                <li>Kein Vorsteuerabzug möglich</li>
              </ul>
              
              <div className="mt-6 pt-6 border-t">
                <a
                  href="https://www.gesetze-im-internet.de/ustg_1980/__19.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#0077FF] text-white rounded-lg hover:bg-[#0066DD] transition-colors font-medium"
                >
                  Mehr erfahren (Gesetzestext)
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateRegisterNew;
